// ProductDemo.js
import React, { useState, useRef } from "react";
import { motion } from "framer-motion";
import ReactPlayer from "react-player";
import {
  HiLightningBolt,
  HiChip,
  HiColorSwatch,
  HiClock,
  HiArrowRight,
} from "react-icons/hi";
import { BsFillPlayFill, BsPauseFill } from "react-icons/bs";

const ProductDemo = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const playerRef = useRef(null);

  const features = [
    {
      icon: <HiChip className="w-6 h-6" />,
      title: "AI-Powered Content Generation",
      description: "One-click document generation with intelligent suggestions and optimizations",
    },
    {
      icon: <HiColorSwatch className="w-6 h-6" />,
      title: "Job-Specific Optimization",
      description: "AI analysis ensures your resume matches target positions perfectly",
    },
    {
      icon: <HiClock className="w-6 h-6" />,
      title: "Versatile AI Assistance",
      description: "Comprehensive support for resumes, cover letters, and application emails",
    },
  ];

  const togglePlay = () => {
    setIsPlaying(!isPlaying);
  };

  return (
    <div className=" bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <span className="inline-flex items-center gap-2 bg-indigo-100 rounded-full px-4 py-2 mb-4">
            <HiLightningBolt className="w-5 h-5 text-indigo-600" />
            <span className="text-indigo-600 text-sm font-medium">Product Demo</span>
          </span>
          <h2 className="text-2xl md:text-4xl font-bold text-neutral-900 mb-4">
            Experience the power of <br />
            <span className="text-indigo-600">intelligent resume building</span>
          </h2>
          <p className="text-sm text-neutral-700 max-w-2xl mx-auto">
            See how ResumeFlex transforms your professional experience into compelling application materials
          </p>
        </motion.div>

        <div className="grid gap-12 items-center max-w-6xl mx-auto mb-20">
          <motion.div
            className="relative"
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <div className="bg-white rounded-2xl ">
              <div 
                className="relative rounded-lg overflow-hidden border-[2px] border-indigo-600 bg-neutral-900 group"
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
              >
                <div className="aspect-video relative">
                  <ReactPlayer
                    ref={playerRef}
                    url="https://vimeo.com/1042963524"
                    width="100%"
                    height="100%"
                    playing={isPlaying}
                    controls={false}
                    playsinline
                    loop
                    muted={false}
                    volume={1}
                    onReady={() => setIsLoading(false)}
                    onBuffer={() => setIsLoading(true)}
                    onBufferEnd={() => setIsLoading(false)}
                    config={{
                      vimeo: {
                        playerOptions: {
                          background: false,
                          responsive: true,
                          quality: "auto",
                          dnt: true,
                          muted: false,
                          autoplay: false,
                        },
                      },
                    }}
                    className="react-player"
                  />

                  {/* Custom Play/Pause Overlay */}
                  <div
                    className={`absolute inset-0 transition-opacity duration-300 ${
                      isHovered || !isPlaying ? 'opacity-100' : 'opacity-0'
                    } bg-gradient-to-t from-black/60 to-transparent group-hover:opacity-100`}
                  />

                  {/* Play/Pause Button */}
                  <motion.button
                    className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${
                      isLoading ? 'cursor-wait' : 'cursor-pointer'
                    }`}
                    onClick={!isLoading ? togglePlay : undefined}
                    initial={false}
                    animate={{ opacity: isHovered || !isPlaying ? 1 : 0 }}
                  >
                    <motion.div
                      className="w-20 h-20 bg-white/90 rounded-full flex items-center justify-center text-indigo-600 hover:bg-white hover:scale-105 transition-all shadow-xl"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {isPlaying ? (
                        <BsPauseFill className="w-10 h-10" />
                      ) : (
                        <BsFillPlayFill className="w-10 h-10 ml-1" />
                      )}
                    </motion.div>
                  </motion.button>

                  {/* Loading Indicator */}
                  {isLoading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                      <div className="w-12 h-12 border-4 border-white/30 border-t-white rounded-full animate-spin" />
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="absolute -z-10 -bottom-6 -right-6 w-full h-full bg-indigo-200 rounded-2xl" />
          </motion.div>

       
        </div>
      </div>
    </div>
  );
};

export default ProductDemo;
